import { DomainHint } from '@api/environmentsApi/models/DomainHint';
import {
	AccountInfo,
	AuthError,
	BrowserAuthErrorCodes,
	Configuration,
	InteractionRequiredAuthErrorCodes,
	PublicClientApplication,
	RedirectRequest,
	SilentRequest,
} from '@azure/msal-browser';

import { logError } from '../logging/logging';

export class AuthHelper {
	public static AAD_DEFAULT_TENANT = 'organizations';
	public static tenantName: string;
	public static user: AccountInfo = null;

	private static CorpTenantId = '72f988bf-86f1-41af-91ab-2d7cd011db47';
	private static PmeTenantId = '975f013f-7f24-47e8-a7d3-abc4752bf346';

	private static tenant: string;
	private static clientApp: PublicClientApplication = null;

	private static REDIRECT_PENDING_KEY = 'msal_redirect_pending';
	private static RELOAD_KEY = 'msal_auth_reload';
	private static onAuthFailure: ((error: AuthError) => void) | null = null;

	// Error codes that indicate the user cannot access the tenant (unrecoverable)
	private static readonly UNRECOVERABLE_ERROR_CODES = new Set(['access_denied', 'user_cancelled']);

	private static isUnrecoverableAuthError = (error: AuthError): boolean => {
		return AuthHelper.UNRECOVERABLE_ERROR_CODES.has(error.errorCode);
	};

	public static clearAllAuthSessionState = () => {
		sessionStorage.removeItem(AuthHelper.REDIRECT_PENDING_KEY);
		sessionStorage.removeItem(AuthHelper.RELOAD_KEY);
	};

	private static get msalConfig(): Configuration {
		const tenant = AuthHelper.tenant || AuthHelper.AAD_DEFAULT_TENANT;

		return {
			auth: {
				clientId: window.siteConfig.appId,
				authority: `https://login.microsoftonline.com/${tenant}`,
				redirectUri: window.location.origin,
				postLogoutRedirectUri: window.location.origin,
			},
			cache: {
				cacheLocation: 'localStorage',
			},
		};
	}

	private static loginRequest: RedirectRequest = {
		scopes: [],
	};

	public static get userName(): string {
		const user = AuthHelper.user;

		if (!user?.idTokenClaims) {
			return null;
		}

		return user.name || user.username || user.idTokenClaims['name'] || null;
	}

	public static get wids(): string[] {
		return AuthHelper.user.idTokenClaims?.['wids'] as string[];
	}

	public static get isAzureGlobalAdmin() {
		// https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference
		return AuthHelper.wids?.includes('62e90394-69f5-4237-9190-012177145e10');
	}

	public static get isAzureAppAdmin() {
		// https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference
		return AuthHelper.wids?.includes('9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3');
	}

	public static get isAzureCloudAppAdmin() {
		// https://docs.microsoft.com/en-us/azure/active-directory/roles/permissions-reference
		return AuthHelper.wids?.includes('158c047a-c907-4556-b7ef-446551a6b5f7');
	}

	public static signOutLinkUrl = (redirectUrlString = window.location.origin) => {
		const tenantPath = AuthHelper.user?.tenantId ? AuthHelper.user.tenantId : 'common';

		return `https://login.microsoftonline.com/${tenantPath}/oauth2/logout?post_logout_redirect_uri=${encodeURIComponent(redirectUrlString)}`;
	};

	private static setCurrentUserAndTenant = (account: AccountInfo) => {
		AuthHelper.user = account;

		/**
		 * In a multi-tenant scenario, user can be signed in under different tenants with the same username.
		 * We need to call acquireTokenSilent with the authority that reflects the tenant of the signed in user (not 'organizations').
		 * Otherwise MSAL does not know which token to return and throw error
		 */
		AuthHelper.tenant = account.tenantId;
	};

	public static initialize = async (
		domainHint: DomainHint,
		onAuthenticatedCallback: (user?: AccountInfo) => void,
		onAuthFailure?: (error: AuthError) => void,
	) => {
		AuthHelper.onAuthFailure = onAuthFailure ?? null;
		AuthHelper.tenant = domainHint?.tenantName; // changes to tenant ID after login
		AuthHelper.tenantName = domainHint?.tenantName; // stays tenant name hint forever

		AuthHelper.clientApp = new PublicClientApplication(AuthHelper.msalConfig);

		try {
			await AuthHelper.clientApp.initialize();
			const redirectResult = await AuthHelper.clientApp.handleRedirectPromise({ navigateToLoginRequestUrl: true });
			sessionStorage.removeItem(AuthHelper.RELOAD_KEY);

			const hintTenantName = domainHint?.tenantName;
			const hintTenantId = domainHint?.tenantId;

			const account = hintTenantId
				? AuthHelper.clientApp.getAccount({ tenantId: hintTenantId })
				: (AuthHelper.clientApp.getAccount({ isHomeTenant: true, tenantId: AuthHelper.PmeTenantId }) ??
					AuthHelper.clientApp.getAccount({ isHomeTenant: true, tenantId: AuthHelper.CorpTenantId }) ??
					AuthHelper.clientApp.getAllAccounts()?.[0]);

			// Go to sign in page when the tenant hint does not match the logged in user's tenant.
			const cachedUserTenantDoesNotMatchDomainHintTenant =
				hintTenantName !== AuthHelper.AAD_DEFAULT_TENANT && hintTenantId !== account?.tenantId;

			if (!account || cachedUserTenantDoesNotMatchDomainHintTenant) {
				const redirectPending = sessionStorage.getItem(AuthHelper.REDIRECT_PENDING_KEY) === 'true';
				if (redirectResult !== null || redirectPending) {
					AuthHelper.clearAllAuthSessionState();
					const error = new AuthError('tenant_access_denied', 'Authentication failed: you may not have access to this tenant.');
					onAuthFailure?.(error);

					return;
				}

				sessionStorage.setItem(AuthHelper.REDIRECT_PENDING_KEY, 'true');
				await AuthHelper.redirectToLogin();

				return;
			}

			AuthHelper.setCurrentUserAndTenant(account);
			AuthHelper.clearAllAuthSessionState();

			onAuthenticatedCallback(account);
		} catch (e) {
			AuthHelper.redirectCallback(e);
		}
	};

	public static acquireTokenSilent = async (scopes?: string[], forceRefresh = false, requestOverrides?: Partial<SilentRequest>) => {
		const request: SilentRequest | RedirectRequest = {
			scopes,
			...requestOverrides,
			forceRefresh,
			account: AuthHelper.user,
			authority: AuthHelper.msalConfig.auth.authority, // Need this for guest account
			redirectUri: window.location.origin,
		};

		try {
			const authResult = await AuthHelper.clientApp.acquireTokenSilent({
				...request,
				redirectUri: `${window.location.origin}/api/auth`, // set redirect Uri for silent token refresh, '/auth' is nothing more than a cached blank page
			});

			return authResult.accessToken;
		} catch (e) {
			if (!AuthHelper.requireInteraction(e.errorCode)) {
				logError(e);
				throw e;
			}

			await AuthHelper.clientApp.acquireTokenRedirect(request);
		}
	};

	public static redirectToLogin = async () => {
		await AuthHelper.clientApp.loginRedirect(AuthHelper.loginRequest);
	};

	public static logoutHandler = (postLogoutRedirectUri?: string) => (): void => {
		AuthHelper.clientApp.logoutRedirect({ postLogoutRedirectUri });
	};

	public static redirectWithAutoLogin = (redirectUri: string) => {
		const url = new URL(redirectUri);

		window.location.assign(url.href);
	};

	private static redirectCallback = (error: AuthError) => {
		if (error) {
			logError(error);

			if (AuthHelper.isUnrecoverableAuthError(error)) {
				AuthHelper.clearAllAuthSessionState();
				AuthHelper.onAuthFailure?.(error);

				return;
			}

			AuthHelper.clearMsalCache();

			// loginRedirect can fail if MSAL has an interaction_in_progress or the browser blocks navigation.
			// Retry once via reload; if that also fails, surface the error to the user.
			AuthHelper.redirectToLogin().catch(() => {
				if (!sessionStorage.getItem(AuthHelper.RELOAD_KEY)) {
					sessionStorage.setItem(AuthHelper.RELOAD_KEY, '1');
					window.location.reload();
				} else {
					AuthHelper.onAuthFailure?.(error);
				}
			});
		}
	};

	private static clearMsalCache = () => {
		Object.keys(localStorage)
			.filter((key) => key.startsWith('msal.'))
			.forEach((key) => localStorage.removeItem(key));
		Object.keys(sessionStorage)
			.filter((key) => key.startsWith('msal.'))
			.forEach((key) => sessionStorage.removeItem(key));
	};

	private static interactionRequiredCodes: Set<string> = new Set(Object.values(InteractionRequiredAuthErrorCodes));

	private static requireInteraction = (errorCode: string): boolean => {
		if (!errorCode) {
			return false;
		}

		if (AuthHelper.interactionRequiredCodes.has(errorCode)) {
			return true;
		}

		return errorCode === 'invalid_grant' || errorCode === BrowserAuthErrorCodes.timedOut;
	};
}
